import { ErrorState, LoadingState } from '../components/StateViews'
import { api } from '../lib/api'
import { number, percent, relativeDate } from '../lib/format'
import { useApi } from '../lib/useApi'

type HistoryPayload = {
  changes: {
    from_plan: string | null
    to_plan: string
    status: string | null
    detected_at: number
  }[]
}

type PlansPayload = {
  current: string
  upgradeUrl: string
  trialDays: number
  plans: {
    handle: string
    name: string
    priceUsdMonthly: number
    alertQuota: number | null
    features: Record<string, boolean>
    current: boolean
  }[]
}

type UsagePayload = {
  alertsSent: number
  alertQuota: number | null
  remaining: number | null
  daysRemaining: number
  subscriptionsCapturedThisPeriod: number
  quotaExceeded: boolean
  approachingQuota: boolean
}

const FEATURE_LABELS: Record<string, string> = {
  waitlistExplorer: 'Waitlist explorer',
  csvExport: 'CSV export',
  templateEditor: 'Email template editor',
  notifyFirstN: 'Alert only the first N shoppers',
  customSendingDomain: 'Send from your own domain',
  demandValuation: 'Waiting demand in dollars',
  restockRecommendations: 'Ranked restock plan with size mix',
  weeklyDigest: 'Weekly restock digest',
  stockoutReport: 'Stockout cost report',
  dataApi: 'Read-only API',
  outboundWebhooks: 'Outbound webhooks',
  sms: 'SMS alerts',
}

/**
 * Entitlements a merchant would be granted but cannot currently use, hidden from the plan table.
 *
 * The entitlement is deliberately left in `plans.ts`: it is what unlocks the feature the day the
 * blocker clears, and deleting it would only mean a second change later. What must not happen is
 * charging for a row the product cannot honour, which is why `sms` came off the marketing page on
 * 2026-08-30. The in-app table advertised both for a further day, because that page builds itself
 * from the entitlements and nobody had looked at it.
 *
 * Delete an entry here when, and only when, the feature actually works:
 *   sms                  waiting on 10DLC carrier registration; consumer.ts refuses to send
 *                        without an approved registration and production holds none
 *   customSendingDomain  enforced in settings.ts, but nothing sets customSendingDomainVerified
 *                        and no code calls Resend's Domains API, so sendingIdentity() always
 *                        falls back to the shared domain. See docs/decisions.md, 2026-08-30.
 */
const NOT_YET_DELIVERABLE = new Set(['sms', 'customSendingDomain'])

/** Plans and usage (SW-701, SW-703). The app never renders a payment form. */
export function Plans() {
  const plans = useApi<PlansPayload>('/plans')
  const usage = useApi<UsagePayload>('/usage')
  const history = useApi<HistoryPayload>('/plans/history')

  if (plans.loading && !plans.data) return <LoadingState />
  if (plans.error) return <ErrorState error={plans.error} onRetry={plans.reload} />
  if (!plans.data) return null

  const payload = plans.data
  const quota = usage.data

  return (
    <s-stack direction="block" gap="large">
      {quota?.quotaExceeded ? (
        // The upgrade prompt that actually works: not "you hit a limit", but "N people are
        // waiting and they are still being captured". Blocking capture would destroy the data
        // and make upgrading pointless (SW-702).
        <s-banner tone="warning" heading="Alerts are paused, but you're still capturing signups">
          <s-stack direction="block" gap="base">
            <s-paragraph>
              You've used all {number(quota.alertQuota ?? 0)} alerts in this billing period.{' '}
              <strong>{number(quota.subscriptionsCapturedThisPeriod)} shoppers</strong> have joined
              waitlists since. We're still recording every one of them, and they'll be alerted as
              soon as you upgrade.
            </s-paragraph>
            <s-stack direction="inline">
              <s-button variant="primary" href={payload.upgradeUrl} target="_top">
                Upgrade to resume sending
              </s-button>
            </s-stack>
          </s-stack>
        </s-banner>
      ) : quota?.approachingQuota ? (
        <s-banner tone="info" heading="You're close to your alert limit">
          {number(quota.alertsSent)} of {number(quota.alertQuota ?? 0)} alerts used,{' '}
          {quota.daysRemaining} days left in this period.
        </s-banner>
      ) : null}

      {quota ? (
        <s-section heading="This billing period">
          <s-stack direction="block" gap="small-300">
            <s-text>
              {number(quota.alertsSent)}
              {quota.alertQuota === null
                ? ' alerts sent'
                : ` of ${number(quota.alertQuota)} alerts used`}
              {quota.alertQuota === null
                ? ''
                : ` (${percent(quota.alertsSent / quota.alertQuota, 0)})`}
            </s-text>
            <s-text color="subdued">{quota.daysRemaining} days until the counter resets.</s-text>
          </s-stack>
        </s-section>
      ) : null}

      <s-grid gridTemplateColumns="repeat(auto-fit, minmax(240px, 1fr))" gap="base">
        {payload.plans.map((plan) => (
          <s-section key={plan.handle}>
            <s-stack direction="block" gap="base">
              <s-stack direction="block" gap="small-500">
                <s-stack direction="inline" gap="small-300" alignItems="center">
                  <s-heading>{plan.name}</s-heading>
                  {plan.current ? <s-badge tone="success">Current</s-badge> : null}
                </s-stack>
                <s-text color="subdued">
                  {plan.priceUsdMonthly === 0 ? 'Free' : `$${plan.priceUsdMonthly}/month`}
                  {plan.alertQuota === null
                    ? ' · unlimited alerts'
                    : ` · ${number(plan.alertQuota)} alerts`}
                </s-text>
              </s-stack>

              <s-unordered-list>
                {Object.entries(plan.features)
                  .filter(([key, enabled]) => enabled && !NOT_YET_DELIVERABLE.has(key))
                  .map(([key]) => (
                    <s-list-item key={key}>
                      <s-text>{FEATURE_LABELS[key] ?? key}</s-text>
                    </s-list-item>
                  ))}
              </s-unordered-list>

              {plan.current ? null : (
                // Deep-links to Shopify's hosted plan page. Shopify handles charges, proration
                // and the 14-day trial; there is no billing code in this app at all.
                <s-button variant="primary" href={payload.upgradeUrl} target="_top">
                  {plan.priceUsdMonthly === 0
                    ? 'Downgrade'
                    : `Start ${payload.trialDays}-day trial`}
                </s-button>
              )}
            </s-stack>
          </s-section>
        ))}
      </s-grid>

      <s-section>
        <s-button
          variant="tertiary"
          onClick={async () => {
            // Shopify App Pricing has no plan-change webhook, so a merchant who just upgraded
            // in another tab needs a way to pull the change through immediately.
            await api.post('/plans/refresh')
            shopify.toast.show('Plan refreshed')
            plans.reload()
            usage.reload()
            history.reload()
          }}
        >
          Refresh plan
        </s-button>
      </s-section>

      {/*
        Billing questions are almost always "when did this change, and to what?". Without this
        the answer lives only in our logs, so the merchant has to ask us and we have to go
        looking, for a fact we already record on their own shop.
      */}
      {history.data && history.data.changes.length > 0 ? (
        <s-section heading="Plan history">
          <s-table>
            <s-table-header-row>
              <s-table-header>Change</s-table-header>
              <s-table-header>Status</s-table-header>
              <s-table-header>When</s-table-header>
            </s-table-header-row>
            <s-table-body>
              {history.data.changes.map((change) => (
                <s-table-row key={`${change.detected_at}-${change.to_plan}`}>
                  <s-table-cell>
                    {change.from_plan ?? 'none'} → {change.to_plan}
                  </s-table-cell>
                  <s-table-cell>{change.status ?? '-'}</s-table-cell>
                  <s-table-cell>{relativeDate(change.detected_at)}</s-table-cell>
                </s-table-row>
              ))}
            </s-table-body>
          </s-table>
        </s-section>
      ) : null}
    </s-stack>
  )
}
